import { useEffect } from "react";

// Per-page metadata for the standalone SEO pages. The SPA shares one
// index.html, so each page stamps its own title/description/canonical/social
// tags on mount. Direct landings (the Google Ads case) get them on first
// render; navigation here is full-page loads, so no restore is needed.
const SITE = "https://thenurturedparentco.com";

const upsertMeta = (key, value, isProperty = false) => {
  const attr = isProperty ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

export const usePageMeta = ({ title, description, path }) => {
  useEffect(() => {
    const url = `${SITE}${path}`;
    document.title = title;
    upsertMeta("description", description);
    upsertMeta("og:title", title, true);
    upsertMeta("og:description", description, true);
    upsertMeta("og:url", url, true);
    upsertMeta("twitter:title", title);
    upsertMeta("twitter:description", description);
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);
  }, [title, description, path]);
};

// FAQPage schema that exactly mirrors the visible FAQ on the page
// (Google requires the marked-up content to be visible to the reader).
// Injected on mount, removed on unmount so pages never carry each other's.
export const useFaqSchema = (faqs) => {
  useEffect(() => {
    if (!faqs?.length) return undefined;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "page-faq-schema";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [faqs]);
};


// BreadcrumbList schema mirroring the visible breadcrumb row (Google requires
// marked-up content to be visible). Injected on mount, removed on unmount.
export const useBreadcrumbSchema = (crumbs) => {
  const key = JSON.stringify(crumbs);
  useEffect(() => {
    const list = JSON.parse(key);
    if (!list?.length) return undefined;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "page-breadcrumb-schema";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: list.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.label,
        ...(c.href ? { item: `${SITE}${c.href}` } : {}),
      })),
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [key]);
};
